<template>
  <Feature
    v-slot="{ feature }"
    :feature-slug="featureNames.VALUATION"
  >
    <SettingsCard
      v-if="
        feature.config.isPlotValuationIntegrationEnabled ||
        (feature.config.isPhoneNumberVerificationEnabled &&
          feature.config.canChangePhoneNumberVerification) ||
        feature.config.isFirstNameVerificationEnabled ||
        feature.config.isLastNameVerificationEnabled ||
        feature.config.isMotivationVerificationEnabled
      "
      :title="$t('settings.features.valuation.title')"
      :subtitle="subtitle"
      icon="mdi-home-variant"
      :model-value="isOpen"
      @update:modelValue="setOpen"
    >
      <div class="pt-4 px-4">
        <v-row v-if="feature.config.isPlotValuationIntegrationEnabled">
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.features.valuation.body-texts.0') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.features.valuation.body-texts.1') }}
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-center px-0 px-md-2"
          >
            <div
              class="d-flex flex-column"
              :style="{ 'max-width': $vuetify.display.smAndDown ? '100%' : '70%' }"
            >
              <div class="d-flex align-center">
                <span style="width: 250px"
                  >{{ $t('settings.features.valuation.labels.add-plot-valuation') }}:</span
                >
                <v-switch
                  v-model="isPlotValuationInValuation"
                  class="ma-0 ml-4 mx-2 custom-switch"
                  density="compact"
                  color="primary"
                  hide-details
                  @update:model-value="updatePlotValuationInValuation"
                />
              </div>
              <r-img
                :src="plotValuationSettingSrc"
                alt="plot valuation image"
                cover
                max-width="100%"
                class="mt-4"
              />
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="
            feature.config.isPhoneNumberVerificationEnabled &&
            feature.config.canChangePhoneNumberVerification &&
            isCountrySupported
          "
        >
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-center px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.features.valuation.body-texts.2') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.features.valuation.body-texts.3') }}<br />
              </span>
              <p class="ma-0 mt-2 text-grey">
                <b>{{ $t('labels.caution') }}:</b>
                {{ $t('settings.features.valuation.body-texts.4') }} <br />
                <b>{{ $t('settings.features.valuation.body-texts.5') }}:</b>
                {{ $t('settings.features.valuation.body-texts.6') }} <br /><br />
              </p>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-center px-0 px-md-2"
            :class="{ 'pa-0 px-0': $vuetify.display.smAndDown }"
          >
            <div
              class="d-flex flex-column"
              :style="{ 'max-width': $vuetify.display.smAndDown ? '100%' : '70%' }"
            >
              <div class="d-flex align-center mb-6">
                <span style="width: 250px"
                  >{{ $t('settings.features.valuation.labels.activate-phone-verification') }}:
                </span>
                <v-switch
                  v-model="isPhoneVerificationEnabled"
                  class="ma-0 ml-4 mx-2 custom-switch"
                  density="compact"
                  color="primary"
                  hide-details
                  @update:model-value="updatePhoneVerificationEnabled"
                />
              </div>
              <r-img
                :src="phoneVerificationSettingSrc"
                alt="phone verification image"
                cover
                max-width="100%"
              />
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="
            feature.config.isFirstNameVerificationEnabled ||
            feature.config.isLastNameVerificationEnabled ||
            feature.config.isMotivationVerificationEnabled
          "
        >
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-center px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.features.valuation.firstname.body-texts.0') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.features.valuation.firstname.body-texts.1') }}<br />
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-center px-0 px-md-2"
            :class="{ 'pa-0 px-0': $vuetify.display.smAndDown }"
          >
            <div class="d-flex flex-column">
              <div
                v-if="feature.config.isFirstNameVerificationEnabled"
                class="d-flex align-center mb-1"
              >
                <v-radio-group
                  v-model="isFirstNameOptionalEnabled"
                  :label="$t('settings.features.valuation.labels.activate-firstname-optional')"
                  @update:model-value="updateFirstNameIsOptionalSetting"
                >
                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="true"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.optional') }}
                    </template>
                  </v-radio>

                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="false"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.required') }}
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>
              <div
                v-if="feature.config.isLastNameVerificationEnabled"
                class="d-flex align-center mb-1"
              >
                <v-radio-group
                  v-model="isLastNameOptionalEnabled"
                  :label="$t('settings.features.valuation.labels.activate-lastname-optional')"
                  @update:model-value="updateLastNameIsOptionalSetting"
                >
                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="true"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.optional') }}
                    </template>
                  </v-radio>

                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="false"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.required') }}
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>
              <div
                v-if="feature.config.isMotivationVerificationEnabled"
                class="d-flex align-center mb-1"
              >
                <v-radio-group
                  v-model="isMotivationOptionalEnabled"
                  :label="$t('settings.features.valuation.labels.activate-motivation-optional')"
                  @update:model-value="updateMotivationIsOptionalSetting"
                >
                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="true"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.optional') }}
                    </template>
                  </v-radio>

                  <v-radio
                    color="primary"
                    class="mb-1 pa-2 suggestion"
                    :value="false"
                  >
                    <template v-slot:label>
                      {{ $t('settings.features.first-last-name.labels.required') }}
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>
            </div>
          </v-col>
        </v-row>
        <ChangeSettingsAlert />
        <ConfirmationDialog
          v-model="showConfirmationDialog"
          :confirmation-dialog-setting="confirmationDialogSetting"
          @update-canceled="handleSettingUpdate(confirmationDialogSetting, false)"
          @update-confirmed="handleSettingUpdate(confirmationDialogSetting, true)"
        />
      </div>
    </SettingsCard>
  </Feature>
</template>

<script lang="ts">
import { computed, inject } from 'vue'

import ChangeSettingsAlert from '@/components/ChangeSettingsAlert.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { Company } from '@/generated/graphql'
import bus, { eventNames } from '@/lib/eventBus'
import featureMixin from '@/mixins/feature'
import ConfirmationDialog from '@/modules/settings/functionalSettings/ConfirmationDialog.vue'
import UPDATE_OPTIONAL_FIELDS from '@/modules/settings/functionalSettings/queries/UpdateOptionalFields.gql'

import UPDATE_PHONE_VERIFICATION from './queries/UpdatePhoneVerification.gql'
import UPDATE_PLOT_VALUATION from './queries/UpdatePlotValuation.gql'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'valuation' })
    const companyContext = inject<Company>('companyBaseData')

    const isCountrySupported = computed(() => {
      if (companyContext.country && companyContext.country === 'us') return false
      return true
    })

    return {
      isOpen,
      setOpen,
      isCountrySupported,
    }
  },
  components: {
    ConfirmationDialog,
    SettingsCard,
    ChangeSettingsAlert,
  },
  mixins: [featureMixin],
  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    refetchSettings: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      plotValuationSettingSrc: 'img/settings-plot-valuation.png',
      phoneVerificationSettingSrc: 'img/settings-phone-verification.png',
      verificationSettingSrc: 'img/settings-verification.png',
      isPhoneVerificationEnabled: this.settings.company.enabledPhoneVerification.VALUATION,
      isPlotValuationInValuation: this.settings.company.plotValuation.showInPropertyValuation,
      isFirstNameOptionalEnabled: this.settings.company?.isFirstNameOptional,
      isLastNameOptionalEnabled: this.settings.company?.isLastNameOptional,
      isMotivationOptionalEnabled: this.settings.company?.isMotivationOptional,
      showConfirmationDialog: false,
      confirmationDialogSetting: '',
    }
  },
  computed: {
    subtitle() {
      return this.getFeature(this.featureNames.VALUATION).config.isPlotValuationIntegrationEnabled
        ? this.$t('settings.features.valuation.subtitle-active')
        : this.$t('settings.features.valuation.subtitle')
    },
  },
  methods: {
    trackChange(name, value) {
      const isEnabled = value ? 'Enabled' : 'Disabled'
      this.$tracking.event('Settings', 'Clicked', name, isEnabled)
    },
    handleSettingUpdate(name, value) {
      if (name === 'firstName' && value === this.settings?.company?.isFirstNameOptional) {
        this.isFirstNameOptionalEnabled = !this.isFirstNameOptionalEnabled
        return
      } else if (name === 'lastName' && value === this.settings?.company?.isLastNameOptional) {
        this.isLastNameOptionalEnabled = !this.isLastNameOptionalEnabled
        return
      }

      this.saveSettings(() => this.saveIsOptionalSetting(name, value))
      const tracking =
        name === 'firstName'
          ? 'First Name Optional'
          : name === 'lastName'
            ? 'Last Name Optional'
            : ''
      this.trackChange(tracking, value)
      this.showConfirmationDialog = false
    },
    updateFirstNameIsOptionalSetting(value) {
      this.isFirstNameOptionalEnabled = value
      this.confirmationDialogSetting = 'firstName'
      if (value && this.isLastNameOptionalEnabled) {
        this.showConfirmationDialog = true
        return
      }
      this.handleSettingUpdate(this.confirmationDialogSetting, value)
    },
    updateLastNameIsOptionalSetting(value) {
      this.isLastNameOptionalEnabled = value
      this.confirmationDialogSetting = 'lastName'
      if (value && this.isFirstNameOptionalEnabled) {
        this.showConfirmationDialog = true
        return
      }
      this.handleSettingUpdate(this.confirmationDialogSetting, value)
    },
    updateMotivationIsOptionalSetting(value) {
      this.isMotivationOptionalEnabled = value
      this.saveSettings(() => this.saveIsOptionalSetting('motivation', value))
      this.trackChange('Motivation Optional', value)
    },
    async saveIsOptionalSetting(name, optional) {
      await this.$apollo.mutate({
        mutation: UPDATE_OPTIONAL_FIELDS,
        variables: {
          input: {
            optionalFields: [
              {
                name,
                optional,
              },
            ],
          },
        },
      })
    },
    updatePhoneVerificationEnabled(value) {
      this.isPhoneVerificationEnabled = value
      this.saveSettings(this.savePhoneVerificationSetting)
      this.trackChange('Phone Verification', value)
    },
    updatePlotValuationInValuation(value) {
      this.isPlotValuationInValuation = value
      this.saveSettings(this.savePlotValuationSetting)
      this.trackChange('Property Valuation', value)
    },
    async savePhoneVerificationSetting() {
      await this.$apollo.mutate({
        mutation: UPDATE_PHONE_VERIFICATION,
        variables: {
          input: { enabled: this.isPhoneVerificationEnabled, companyId: this.$auth.user.companyId },
        },
      })
    },
    async savePlotValuationSetting() {
      await this.$apollo.mutate({
        mutation: UPDATE_PLOT_VALUATION,
        variables: {
          input: {
            showInPropertyValuation: this.isPlotValuationInValuation,
            companyId: this.$auth.user.companyId,
          },
        },
      })
    },
    async saveSettings(save) {
      try {
        await save()
        this.refetchSettings()
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.features.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
      }
    },
  },
}
</script>
<style scoped>
.custom-switch {
  margin-top: -2px !important;
}

.custom-switch :deep(.v-switch__track) {
  opacity: 0.3;
}

.custom-switch :deep(.v-switch__thumb) {
  color: white;
}

.custom-switch :deep(.v-input--switch__track) {
  background-color: rgba(0, 0, 0, 0.12) !important;
}

.custom-switch.v-input--switch--model-value :deep(.v-input--switch__track) {
  background-color: rgb(var(--v-theme-primary)) !important;
  opacity: 0.3;
}
</style>
